import _ from 'underscore';
import { isInstanceOfStep, isInstanceOfStepContainer } from './is-instance-of';

/**
 * A tree structure representing the steps and nested steps below.
 * @typedef {Object} StepsTree
 * @param {Array} steps - List of immediate child steps, can contain additional steps.
 * @param {Boolean} containsSelected - True if this is an ancestor of the currently selected path.
 * @param {Object} currentStep - Step that corresponds to the currently selected path.
 */

/**
 * Recursively search through a list of children to build a tree out of Steps and StepContainers.
 * Note: Will only check direct descendants only, will not check the children of elements that are
 * not Step or StepContainer.
 * @param {ReactNode|ReactNode[]} children - Initial array of children to iterate through.
 * @param {String} currentPath - Currently selected path in the step wizard.
 * @returns {StepsTree} - A tree structure containing all the steps.
 */
export function getStepsTree(children, currentPath) {
  if (_.isEmpty(children) || _.isEmpty(currentPath)) {
    return null;
  }

  const tree = {
    steps: [],
    containsSelected: false,
    currentStep: null,
  };

  _.each(_.isArray(children) ? children : [children], (child) => {
    if (isInstanceOfStep(child) && child.props.enabled) {
      const step = {
        path: child.props.path,
        title: child.props.title,
        description: child.props.description,
        isSelected: child.props.path === currentPath,
        containsSelected: false,
        renderTitle: child.props.renderTitle,
        renderGlyph: child.props.renderGlyph,
        renderDescription: child.props.renderDescription,
      };

      if (step.isSelected) {
        tree.currentStep = step;
        tree.containsSelected = true;
      }

      // recurse if child contains a single StepContainer as a child
      const grandchild = child.props.children;
      if (isInstanceOfStepContainer(grandchild)) {
        const subTree = getStepsTree(grandchild.props.children, currentPath);
        if (subTree != null) {
          tree.currentStep = tree.currentStep || subTree.currentStep;
          tree.containsSelected = tree.containsSelected || subTree.containsSelected;

          step.steps = subTree.steps;
          step.containsSelected = step.containsSelected || subTree.containsSelected;
        }
      }

      tree.steps.push(step);
    }
  });

  return tree;
}
